import * as React from 'react';
import { Box, TextField, FormControl, FormHelperText, InputLabel, Select, MenuItem } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { useCompanyLookup } from '../../hooks/useCompanyLookup';
import { BusinessIdLookupField, EInvoiceLookupButton } from './CompanyLookupFields';
import { hasCustomerIdentity, hasCustomerName } from '../../utils/customer';

/**
 * Inline new-customer form shared by every dialog that can create a customer on
 * the fly (work order, calendar, invoice, billing customer, tire hotel).
 *
 * Controlled component: `value` is the customer draft object, `onChange`
 * receives the full updated object. Use EMPTY_NEW_CUSTOMER as the initial
 * (and reset) value, and isNewCustomerValid() to gate submission.
 *
 * `requireName`: the dialog makes out an invoice to the customer right away, so the
 * name is required (it is the invoice's buyer). Otherwise any of name, phone or
 * email will do, like the API rule (customers.validators.has_customer_identity).
 *
 * Company customers get the same Y-tunnus lookups as the full customer dialog:
 * YTJ fills the name and address, Maventa the e-invoice address.
 *
 * `fieldSx` styles every input (e.g. rounded corners to match the host dialog).
 */

export const EMPTY_NEW_CUSTOMER = {
  type: 'individual',
  email: '',
  phone: '',
  address_street: '',
  address_postal_code: '',
  address_city: '',
  first_name: '',
  last_name: '',
  company_name: '',
  contact_person: '',
  business_id: '',
  verkkolasku_address: '',
  verkkolasku_operator: '',
};

export function isNewCustomerValid(customer, { requireName = false } = {}) {
  // Identifying fields only. Several dialogs treat an invalid draft as "no new customer"
  // and carry on without one (see isNewCustomerIncomplete); a bad Y-tunnus is flagged on
  // the field and rejected by the API.
  // A name, a phone number or an email: a caller often gives only a number
  return requireName ? hasCustomerName(customer) : hasCustomerIdentity(customer);
}

/**
 * Details typed into the draft but nothing to identify the customer by. Dialogs that
 * carry on without an invalid draft must refuse to save in this case, otherwise the
 * typed address etc. are thrown away without a word.
 */
export function isNewCustomerIncomplete(customer) {
  const hasData = Object.entries(customer).some(
    ([field, fieldValue]) => field !== 'type' && typeof fieldValue === 'string' && fieldValue.trim() !== ''
  );
  return hasData && !isNewCustomerValid(customer);
}

function NewCustomerForm({ t, value, onChange, size = 'medium', fieldSx, requireName = false }) {
  // The lookups answer asynchronously; merge into the draft as it is by then.
  const latestValue = React.useRef(value);
  latestValue.current = value;

  const companyLookup = useCompanyLookup({
    t,
    customer: value,
    applyUpdates: (updates) => onChange({ ...latestValue.current, ...updates }),
  });

  const handleFieldChange = (field) => (e) => {
    onChange({ ...value, [field]: e.target.value });
  };

  // Details typed but nothing to identify the customer by: the host dialog will not save,
  // so the identifying fields are marked and the hint below them says why
  const incomplete = !requireName && isNewCustomerIncomplete(value);
  const identityError = incomplete ? { error: true } : {};
  // With requireName an individual needs one of the two names; both stay marked until one is given
  const individualNameRequired = requireName && !value.first_name?.trim() && !value.last_name?.trim();

  const gap = size === 'small' ? 1.5 : 2;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
      {/* Customer Type */}
      <FormControl fullWidth size={size} sx={fieldSx}>
        <InputLabel>{t('Customer Type')}</InputLabel>
        <Select
          value={value.type}
          onChange={handleFieldChange('type')}
          label={t('Customer Type')}
        >
          <MenuItem value="individual">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" />
              {t('Individual')}
            </Box>
          </MenuItem>
          <MenuItem value="company">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" />
              {t('Company')}
            </Box>
          </MenuItem>
        </Select>
      </FormControl>

      {/* Individual fields */}
      {value.type === 'individual' && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap }}>
          <TextField
            label={t('First Name')}
            value={value.first_name}
            onChange={handleFieldChange('first_name')}
            required={individualNameRequired}
            {...identityError}
            fullWidth
            size={size}
            sx={fieldSx}
          />
          <TextField
            label={t('Last Name')}
            value={value.last_name}
            onChange={handleFieldChange('last_name')}
            required={individualNameRequired}
            {...identityError}
            fullWidth
            size={size}
            sx={fieldSx}
          />
        </Box>
      )}

      {/* Company fields: Business ID first, it unlocks the YTJ autofill for the rest */}
      {value.type === 'company' && (
        <>
          <BusinessIdLookupField
            t={t}
            lookup={companyLookup}
            value={value.business_id}
            onChange={(businessId) => onChange({ ...value, business_id: businessId })}
            size={size}
            sx={fieldSx}
          />
          <TextField
            label={t('Company Name')}
            value={value.company_name}
            onChange={handleFieldChange('company_name')}
            required={requireName}
            {...identityError}
            fullWidth
            size={size}
            sx={fieldSx}
          />
          <TextField
            label={t('Contact Person')}
            value={value.contact_person}
            onChange={handleFieldChange('contact_person')}
            fullWidth
            size={size}
            sx={fieldSx}
          />
        </>
      )}

      {/* Contact fields: either one alone identifies the customer */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap }}>
        <TextField
          label={t('Email')}
          type="email"
          value={value.email}
          onChange={handleFieldChange('email')}
          {...identityError}
          fullWidth
          size={size}
          sx={fieldSx}
        />
        <TextField
          label={t('Phone')}
          value={value.phone}
          onChange={handleFieldChange('phone')}
          {...identityError}
          fullWidth
          size={size}
          sx={fieldSx}
        />
      </Box>
      {incomplete && (
        <FormHelperText error sx={{ mt: -0.5, mx: 1.75 }} data-testid="new-customer-identity-hint">
          {t('customer.identityHint')}
        </FormHelperText>
      )}

      {/* Address fields */}
      <TextField
        label={t('Street Address')}
        value={value.address_street}
        onChange={handleFieldChange('address_street')}
        fullWidth
        size={size}
        sx={fieldSx}
      />
      <Box sx={{ display: 'flex', gap }}>
        <TextField
          label={t('Postal Code')}
          value={value.address_postal_code}
          onChange={handleFieldChange('address_postal_code')}
          size={size}
          sx={{ ...fieldSx, flex: 1 }}
        />
        <TextField
          label={t('City')}
          value={value.address_city}
          onChange={handleFieldChange('address_city')}
          size={size}
          sx={{ ...fieldSx, flex: 2 }}
        />
      </Box>

      {/* E-invoice address (company customers) */}
      {value.type === 'company' && (
        <>
          <EInvoiceLookupButton t={t} lookup={companyLookup} />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap }}>
            <TextField
              label={t('E-Invoice Address')}
              value={value.verkkolasku_address || ''}
              onChange={handleFieldChange('verkkolasku_address')}
              fullWidth
              size={size}
              placeholder="003712345678 / FI37..."
              inputProps={{ maxLength: 34 }}
              sx={fieldSx}
            />
            <TextField
              label={t('Operator Code')}
              value={value.verkkolasku_operator || ''}
              onChange={handleFieldChange('verkkolasku_operator')}
              fullWidth
              size={size}
              placeholder="DABAFIHH"
              inputProps={{ maxLength: 20 }}
              sx={fieldSx}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default NewCustomerForm;
